import {
  Component,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnInit
} from '@angular/core';

import { Subject } from 'rxjs/Subject';

import {
  SkyDropdownMessage,
  SkyDropdownMessageType
} from '../dropdown';

import { SkyTimepickerTimeOutput } from './timepicker.interface';

const moment = require('moment');


export class SkyTimepickerComponent implements OnInit {
  
  public selectedTimeChanged: EventEmitter<SkyTimepickerTimeOutput> =
    new EventEmitter<SkyTimepickerTimeOutput>();

  public dropdownController = new Subject<SkyDropdownMessage>();
  public activeTime: Date;
  public returnFormat: string;
  public timeFormat: string = 'hh';
  public hours: Array<number>;
  public minutes: Array<number>;
  public localeFormat: string;
  public minuteMultiplier: number;
  public is8601: boolean = false;

  public ngOnInit() {
    this.setFormat(this.timeFormat);
  }

  public setFormat(format: string) {
    let h: number = 12;
    let m: number = 12;
    let minuteMultiplier: number = 5;
    let localeFormat: string = 'h:mm A';
    if (format === 'hh') { h = 12; m = 12; minuteMultiplier = 5; localeFormat = 'h:mm A'; }
    if (format === 'HH') {
      h = 24;
      m = 4;
      minuteMultiplier = 15;
      localeFormat = 'HH:mm';
      this.is8601 = true;
    }
    let data: {
      'hours': Array<number>,
      'minutes': Array<number>,
      'localeFormat': string,
      'minuteMultiplier': number
    };

    data = {
      'hours': Array.apply(undefined, Array(h))
        .map(function (x: number, i: number) {
          if (format === 'hh') { return ++i; }
          /* istanbul ignore else */
          if (format === 'HH') { return i; }
          /* istanbul ignore next */
          /* sanity check */
          return 0;
        }),
      'minutes': Array.apply(undefined, Array(m))
        .map(function (x: number, i: number) {
          return i * minuteMultiplier;
        }),
      'localeFormat': localeFormat,
      'minuteMultiplier': minuteMultiplier
    };

    this.hours = data.hours;
    this.minutes = data.minutes;
    this.localeFormat = data.localeFormat;
    this.minuteMultiplier = data.minuteMultiplier;
  }

  public set selectedTime(newTime: SkyTimepickerTimeOutput) {
    if (typeof newTime !== 'undefined') {
      /* sanity check */
      /* istanbul ignore else */
      if (newTime.local !== 'Invalid date') {
        this.activeTime = newTime.iso8601;
      }
    }
  }

  public get selectedTime() {
    const time: SkyTimepickerTimeOutput = {
      hour: moment(this.activeTime).hour(),
      minute: moment(this.activeTime).minute(),
      meridie: moment(this.activeTime).format('A'),
      timezone: parseInt(moment(this.activeTime).format('Z'), 10),
      iso8601: this.activeTime,
      local: moment(this.activeTime).format(this.localeFormat),
      customFormat: (typeof this.returnFormat !== 'undefined')
        ? this.returnFormat : this.localeFormat
    };

    return time;
  }

  public setTime(event: any) {
    /* istanbul ignore else */
    if (typeof event !== 'undefined') {
      /* istanbul ignore else */
      if (event.type === 'click') {
        event.stopPropagation();
        if (event.target.name === 'hour') {
          this.selectedHour = parseInt(event.target.innerHTML, 0);
        }
        if (event.target.name === 'minute') {
          this.selectedMinute = parseInt(event.target.innerHTML, 0);
        }
        if (event.target.name === 'meridie') {
          this.selectedMeridies = event.target.innerHTML;
        }
      }
    }
  }

  public onButtonClick() {
    this.dropdownController.next({
      type: SkyDropdownMessageType.Close
    });
  }

  public set selectedHour(setHour: number) {
    let hour: number;
    let hourOffset: number = 0;
    if (this.selectedMeridies === 'AM' && setHour === 12) { hourOffset = -12; }
    if (this.selectedMeridies === 'PM' && setHour !== 12) { hourOffset = 12; }
    if (this.is8601) { hourOffset = 0; }
    hour = moment({ 'hour': setHour }).add(hourOffset, 'hours').hour();

    this.activeTime = moment({
      'hour': hour,
      'minute': moment(this.activeTime).get('minute') + 0
    }).toDate();
    this.selectedTimeChanged.emit(this.selectedTime);
  }

  public set selectedMinute(minute: number) {
    this.activeTime = moment({
      'hour': moment(this.activeTime).get('hour') + 0,
      'minute': minute
    }).toDate();
    this.selectedTimeChanged.emit(this.selectedTime);
  }

  public set selectedMeridies(meridies: string) {
    /* istanbul ignore else */
    if (!this.is8601) {
      if (meridies !== this.selectedMeridies) {
        this.activeTime = moment(this.activeTime).add(12, 'hours').toDate();
        this.selectedTimeChanged.emit(this.selectedTime);
      }
    }
  }

  public get selectedHour() {
    if (!this.is8601) {
      /* istanbul ignore next */
      return parseInt(moment(this.activeTime).format('h'), 0) || 1;
    }
    /* istanbul ignore else */
    if (this.is8601) {
      return moment(this.activeTime).hour() + 0;
    }
  }

  public get selectedMinute() {
    return moment(this.activeTime).minute() + 0;
  }

  public get selectedMeridies() {
    if (this.activeTime) {
      return moment(this.activeTime).format('A');
    }
    return '';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-timepicker',
  template: `<div class="sky-input-group">
  <ng-content></ng-content>
  <div class="sky-input-group-btn sky-input-group-timepicker-btn">
    <sky-dropdown
      buttonType="clock-o"
      alignment="right"
      [messageStream]="dropdownController">
      <sky-dropdown-menu>
        <sky-dropdown-item>
          <div class="sky-timepicker-container">
            <section class="sky-timepicker-column" [ngClass]="{'sky-timepicker-24hour':is8601}">
              <ol>
                <li *ngFor="let hour of hours;">
                  <button type="button" name="hour" (click)="setTime($event);" [ngClass]="{'sky-btn-active': selectedHour === hour}">{{hour}}</button>
                </li>
              </ol>
            </section>
            <section class="sky-timepicker-column">
              <ol>
                <li *ngFor="let minute of minutes;">
                  <button type="button" name="minute" (click)="setTime($event);" [ngClass]="{'sky-btn-active': selectedMinute === minute}">{{ '00' .substring(0, 2 - (minute) .toString() .length) + (minute) }}</button>
                </li>
              </ol>
            </section>
            <section *ngIf="!is8601" class="sky-timepicker-column">
              <ol>
                <li>
                  <button type="button" name="meridie" (click)="setTime($event);" [ngClass]="{'sky-btn-active': selectedMeridies === 'AM'}">AM</button>
                </li>
                <li>
                  <button type="button" name="meridie" (click)="setTime($event);" [ngClass]="{'sky-btn-active': selectedMeridies === 'PM'}">PM</button>
                </li>
              </ol>
            </section>
          </div>
          <div class="sky-timepicker-container sky-timepicker-footer">
            <section class="sky-timepicker-column">
              <button
                type="button"
                (click)="onButtonClick()">
                {{'timepicker_close' | skyResources}}
              </button>
            </section>
          </div>
        </sky-dropdown-item>
      </sky-dropdown-menu>
    </sky-dropdown>
  </div>
</div>
`,
  styles: [`.sky-input-group-timepicker-btn /deep/ .sky-btn {
  border-radius: 0;
}

.sky-input-group-timepicker-btn /deep/ .sky-dropdown-menu {
  box-shadow: none;
  background-color: transparent;
  text-align: center;
}

.sky-input-group /deep/ .sky-dropdown-item {
  box-shadow: 0 0 3px 0;
}

.sky-timepicker-container {
  font-size: 15px;
  display: flex;
  padding: 5px;
  background-color: #eeeeef;
}

.sky-timepicker-container button {
  background-color: #ffffff;
  border-width: 0;
  padding: 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  width: 100%;
  height: 100%;
}

.sky-timepicker-container :last-child ol {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sky-timepicker-container :last-child ol li {
  border-bottom: 1px solid #e2e3e4;
  flex: 1;
}

.sky-timepicker-column {
  margin: 5px;
}

.sky-timepicker-column ol {
  border-top: 1px solid #e2e3e4;
  border-right: 1px solid #e2e3e4;
  border-bottom: 1px solid #e2e3e4;
  border-left: 1px solid #e2e3e4;
  column-gap: 1px;
  columns: 2;
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.sky-timepicker-column ol li {
  text-align: center;
  cursor: pointer;
  margin: 0;
}

.sky-timepicker-column ol li button {
  cursor: pointer;
}

.sky-timepicker-column ol li button:focus {
  outline: thin dotted;
  outline: -webkit-focus-ring-color auto 5px;
  outline-offset: -2px;
}

.sky-timepicker-column ol li button:hover {
  background-color: #eeeeef;
}

.sky-timepicker-column ol li button:active {
  background-color: #e2e3e4;
}

.sky-timepicker-column ol li button.sky-btn-active:hover {
  background-color: #007ca6;
}

.sky-timepicker-column ol .sky-btn-active {
  background-color: #007ca6;
  color: #ffffff;
}

.sky-timepicker-column.sky-timepicker-24hour ol {
  columns: 4;
}

.sky-timepicker-column.sky-timepicker-24hour ol li {
  border-bottom-width: 0px;
}

.sky-timepicker-footer {
  margin: 0px;
  padding: 10px;
  padding-top: 0px;
}

.sky-timepicker-footer .sky-timepicker-column {
  width: 100%;
}

.sky-timepicker-footer button {
  border-style: solid;
  border-width: 1px;
  border-color: #e2e3e4;
  padding: 10px;
  cursor: pointer;
}
`],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'selectedTimeChanged': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
